import React, { useCallback, useState, useMemo, type PropsWithChildren, Fragment } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql, useMutation } from 'react-relay';
import traceUFOPress from '@atlaskit/react-ufo/trace-press';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import { useFieldInlineEditActions } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/index.tsx';
import type { OnSubmitCallbacks } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/types.tsx';
import type { ValidationFieldProps } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/field-inline-edit-lite/types.tsx';
import { FieldInlineEditLiteWithEntryPoint } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/index.tsx';
import SprintEditViewEntryPoint from '@atlassian/jira-issue-field-sprint-editview-full/src/entrypoint.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import type {
	NullableOption,
	SprintEditViewProps,
} from '@atlassian/jira-issue-field-sprint-editview-full/src/ui/sprint/types.tsx';
import {
	findActiveOrFutureSprint,
	isObjectWithSprintState,
} from '@atlassian/jira-issue-field-sprint-readview-full/src/common/utils.tsx';
import { SprintReadView } from '@atlassian/jira-issue-field-sprint-readview-full/src/ui/sprint/index.tsx';
import { CellHoverProvider } from '@atlassian/jira-hover-popover/src/controllers/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { functionWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { useIssueExperienceMonitoring } from '@atlassian/jira-issue-reliability-fetch/src/use-issue-experience-monitoring.tsx';
import type { sprint_issueFieldSprintInlineEditFull_SprintInlineEditViewIsEditable_fragmentRef$key as SprintWithIsEditableFragment } from './__generated__/sprint_issueFieldSprintInlineEditFull_SprintInlineEditViewIsEditable_fragmentRef.graphql';
import type { sprint_issueFieldSprintInlineEditFull_SprintInlineEditView_fragmentRef$key as SprintFragment } from './__generated__/sprint_issueFieldSprintInlineEditFull_SprintInlineEditView_fragmentRef.graphql';
import type { sprint_issueFieldSprint_SprintField_Mutation as SprintMutation } from './__generated__/sprint_issueFieldSprint_SprintField_Mutation.graphql';
import type {
	SprintInlineEditViewProps,
	SprintInlineEditViewWithIsEditableProps,
} from './types.tsx';
import { toOptimisticResponse, transformOptionToAgg } from './utils.tsx';

const isEqualSprint = (a: NullableOption, b: NullableOption) => a?.value === b?.value;

const MaybeUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="issue-field-sprint-read-view" mode="list">
			{children}
		</UFOSegment>
	),
	Fragment,
);

export const useIssueExperienceMonitoringWithFg = functionWithCondition(
	() => fg('thor-sprint-field-use-monitoring-wrapper'),
	useIssueExperienceMonitoring,
	() => ({ enrichCommitWithIssueExperienceMonitoring: (mutationArgs) => mutationArgs }),
);

/**
 * Inline edit will handle the switching behaviour between the 'readView' and 'editView' components. This variant allows
 * consumers to define their own value to determine whether the field is editable.
 *
 * In most cases consumers should use `SprintInlineEditView` which will enforce that the user has permission to
 * edit the field within the issue view. However, this component can be used for experiences that have differing
 * permissions or want finer control for how this data is retrieved, e.g. lazy loading editability.
 *
 * @param props [SprintInlineEditViewWithIsEditableProps](./types.tsx)
 */
export const SprintInlineEditViewIsEditable = ({
	attributes,
	spacing = 'compact',
	editViewPopup,
	editViewPopupAlignBlock,
	fragmentRef,
	getUrl,
	isEditable: isFieldEditable,
	isEditing: startWithEditViewOpen,
	menuPosition,
	menuPortalTarget,
	onRestrictSprints,
	onSubmit,
	onSubmitFailed,
	onSubmitSucceeded,
	projectKey,
	readViewFitContainerHeight,
	restrictSprints,
	isTruncated,
}: SprintInlineEditViewWithIsEditableProps): React.JSX.Element => {
	// #region Relay
	const data = useFragment<SprintWithIsEditableFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment sprint_issueFieldSprintInlineEditFull_SprintInlineEditViewIsEditable_fragmentRef on JiraSprintField {
				...sprint_issueFieldSprintReadviewFull_SprintReadView
				id
				fieldId
				type
				name
				selectedSprintsConnection {
					edges {
						node {
							# Ensure we fetch all fields required for the read view so we can produce a valid
							# selectedSprintsConnection when optimisitcally updating the sprint.
							id
							sprintId
							name
							state
							# eslint-disable-next-line @atlassian/relay/unused-fields
							endDate
						}
					}
				}
			}
		`,
		fragmentRef,
	);
	const { enrichCommitWithIssueExperienceMonitoring } = useIssueExperienceMonitoringWithFg();
	const { id: uniqueFieldId, fieldId, type, name: fieldName, selectedSprintsConnection } = data;

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [commit] = useMutation<SprintMutation>(graphql`
		mutation sprint_issueFieldSprint_SprintField_Mutation($input: JiraUpdateSprintFieldInput!)
		@raw_response_type {
			jira @optIn(to: ["JiraIssueFieldMutations"]) {
				updateSprintField(input: $input) {
					success
					errors {
						message
					}
					field {
						id
						...sprint_issueFieldSprintReadviewFull_SprintReadView
					}
				}
			}
		}
	`);

	const sprintEdges = useMemo(
		() => selectedSprintsConnection?.edges?.filter(Boolean) ?? [],
		[selectedSprintsConnection?.edges],
	);

	const initialValue = useMemo((): NullableOption => {
		const sprintNodes = sprintEdges.map(({ node }) => node).filter(Boolean);
		const currentSprint = findActiveOrFutureSprint(sprintNodes);

		return currentSprint && isObjectWithSprintState(currentSprint)
			? {
					value: currentSprint.id,
					label: currentSprint.name ?? '',
					state: currentSprint.state,
					sprintId: currentSprint.sprintId,
				}
			: null;
	}, [sprintEdges]);
	const [updatedValue, setUpdatedValue] = useState<NullableOption>(initialValue);

	const handleSubmit = useCallback(
		(option: NullableOption, { onSuccess, onFail }: OnSubmitCallbacks) => {
			if (fg('thor-sprint-field-use-monitoring-wrapper')) {
				traceUFOPress('issue.field.sprint.edit');
			}

			onSubmit?.(transformOptionToAgg(selectedSprintsConnection?.edges ?? [], option));

			commit(
				enrichCommitWithIssueExperienceMonitoring(
					{
						variables: {
							input: {
								id: uniqueFieldId,
								operation: {
									operation: 'SET',
									id: option?.value ?? null,
								},
							},
						},
						onCompleted: (mutationData) => {
							if (mutationData.jira?.updateSprintField?.success) {
								onSuccess();
							} else {
								onFail();
							}
						},
						onError(error) {
							onFail(error);
						},
						optimisticResponse: toOptimisticResponse(uniqueFieldId, option, sprintEdges),
					},
					'sprint_issueFieldSprint_SprintField_Mutation',
					{
						sendUFOEvent: true,
					},
					() => fg('thor-sprint-field-use-monitoring-wrapper'),
				),
			);
		},
		[
			commit,
			onSubmit,
			selectedSprintsConnection?.edges,
			sprintEdges,
			uniqueFieldId,
			enrichCommitWithIssueExperienceMonitoring,
		],
	);

	const handleSubmitSucceeded = useCallback(
		(value: NullableOption) => {
			onSubmitSucceeded?.(transformOptionToAgg(selectedSprintsConnection?.edges ?? [], value));
		},
		[onSubmitSucceeded, selectedSprintsConnection?.edges],
	);

	const setAnalyticsAttributes = useCallback(
		(value: NullableOption) => {
			getUpdateAnalyticsFlowHelper().setActionTakenAttributes(
				fieldId,
				initialValue?.sprintId,
				value?.sprintId || null,
			);
		},
		[fieldId, initialValue],
	);

	const {
		handleCancel,
		handleEdit,
		handleConfirm,
		handleChangeAndConfirm,
		handleCopy,
		handleCut,
		handlePaste,
		hasServerValidationError,
		invalidMessage,
		isEditing,
	} = useFieldInlineEditActions({
		attributes,
		fieldId,
		fieldName,
		fieldType: type,
		initialValue,
		isValueEqual: isEqualSprint,
		onSubmit: handleSubmit,
		onSubmitFailed,
		onSubmitSucceeded: handleSubmitSucceeded,
		onUpdateValue: setUpdatedValue,
		startWithEditViewOpen,
		updatedValue,
		clipboardComponentType: 'sprint',
		setAnalyticsAttributes,
	});

	const renderReadView = useCallback(
		() => (
			<MaybeUFOSegment>
				<CellHoverProvider>
					<SprintReadView fragmentRef={data} getUrl={getUrl} isTruncated={isTruncated} />
				</CellHoverProvider>
			</MaybeUFOSegment>
		),
		[data, getUrl, isTruncated],
	);

	const getEditViewProps = (fieldProps: ValidationFieldProps): SprintEditViewProps => ({
		...fieldProps,
		spacing,
		fieldId: uniqueFieldId,
		fieldName,
		autoFocus: true,
		value: updatedValue,
		onChange: handleChangeAndConfirm,
		projectKey,
		onRestrictSprints,
		restrictSprints,
		menuPosition,
		menuPortalTarget,
	});

	return (
		<FieldInlineEditLiteWithEntryPoint
			editViewPopup={editViewPopup}
			editViewPopupAlignBlock={editViewPopupAlignBlock}
			editViewEntryPoint={SprintEditViewEntryPoint}
			editViewEntryPointParams={{}}
			getEditViewProps={getEditViewProps}
			fieldName={fieldName}
			hasUnsubmittedChanges={hasServerValidationError}
			invalidMessage={invalidMessage}
			isEditing={isEditing}
			isEditable={isFieldEditable}
			onCancel={handleCancel}
			onConfirm={handleConfirm}
			onEdit={handleEdit}
			onCopy={handleCopy}
			onCut={handleCut}
			onPaste={handlePaste}
			readViewFitContainerHeight={readViewFitContainerHeight}
			readView={renderReadView}
			hasReadViewTruncateDisabled={isTruncated}
			hideActionButtons
		/>
	);
};

/**
 * Inline edit will handle the switching behaviour between the 'readView' and 'editView' components.
 *
 * @param props [SprintInlineEditViewProps](./types.tsx)
 */

export const SprintInlineEditView = ({
	fragmentRef,
	...props
}: SprintInlineEditViewProps): React.JSX.Element => {
	const data = useFragment<SprintFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment sprint_issueFieldSprintInlineEditFull_SprintInlineEditView_fragmentRef on JiraSprintField {
				...sprint_issueFieldSprintInlineEditFull_SprintInlineEditViewIsEditable_fragmentRef
				fieldConfig {
					isEditable
				}
			}
		`,
		fragmentRef,
	);

	return (
		<SprintInlineEditViewIsEditable
			{...props}
			fragmentRef={data}
			isEditable={data.fieldConfig?.isEditable ?? false}
		/>
	);
};
